import React, { Suspense, lazy, useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import WikiFrontPage from './components/wiki/WikiFrontPage';
import Judgment from './pages/Judgment';
import JudgmentMirror from './pages/JudgmentMirror';
import NotFound from './pages/NotFound';
import { forgetReturnPath, isAppRoute, readReturnPath, rememberReturnPath } from './navigation/appRoutes';
import { Analytics } from '@vercel/analytics/react';
import { GUIDE_SLUGS } from './seo/guideSlugs';
import Register from './components/Register';
import Login from './components/Login';
import Landing from './pages/Landing';
import CommandPalette from './components/CommandPalette';
import KeyboardShortcutOverlay from './components/KeyboardShortcutOverlay';
import ProductFeedbackModal from './components/ProductFeedbackModal';
import ThemePicker from './components/settings/ThemePicker';
import { clearStoredTokens, hasUsableStoredToken } from './api';
import { fetchUiSettings, saveUiSettings } from './api/uiSettings';
import {
  applyUiSettingsToRoot,
  loadUiSettingsFromStorage,
  normalizeUiSettings,
  persistUiSettingsToStorage
} from './settings/uiPreferences';
import { formatAppearanceValue } from './settings/appearanceFieldModel';
import { TemporaryAppearanceProvider, TemporaryAppearanceBar } from './settings/TemporaryAppearanceContext';
import { Page } from './components/ui';
import AppShell from './layout/AppShell';
import TopBar from './layout/TopBar';
import TourProvider from './tour/TourProvider';
import TourManager from './tour/TourManager';
import FirstRunGate from './onboarding/FirstRunGate';
import { buildCanonicalHighlightPath } from './utils/sourceRoutes';
import {
  adjacentRoom,
  buildThinkPosturePath,
  consumeGoToChord,
  focusRoomSearch,
  GO_TO_CHORD_MS,
  GO_TO_PRIMED_CLASS,
  getPrimaryNavItems,
  getSecondaryNavItems,
  getTopBarUtilityNavItems,
  isGoToTypingTarget,
  openFocusedItem,
  resolveMove,
  walkItems
} from './navigation/appNavigation';
import { namesAThinkObject } from './pages/thinkNotesModel';
import { useSystemStatus } from './system/useSystemStatus';
import { useStorageStatus } from './system/useStorageStatus';
import { SystemStatusProvider } from './system/SystemStatusContext';
import { AgentRailProvider } from './agent/AgentRailContext';
import PartnerPanel from './agent/PartnerPanel';
import { hasPartnerPanel } from './agent/contextualAgentContracts';
import { NoeisSurfaceProvider, useNoeisSurfaceState } from './surface/NoeisSurfaceContext';
import { NoeisCapabilityProvider } from './system/NoeisCapabilityProvider';
import { NoeisLoopProvider } from './system/NoeisLoopProvider';
import './styles/theme.css';
import './styles/global.css';
import './App.css';
import './styles/reading-layout.css';
import './styles/dashboard-refresh.css';
import './styles/editions.css';
import './styles/think-notes.css';
import './surface/surface-frame.css';
import './styles/semantic-theme.css';

const Library = lazy(() => import('./pages/Library'));
const LibraryTrail = lazy(() => import('./pages/LibraryTrail'));
const SaveLink = lazy(() => import('./pages/SaveLink'));
const Editions = lazy(() => import('./pages/Editions'));
const EditionRead = lazy(() => import('./pages/EditionRead'));
const ThinkMode = lazy(() => import('./pages/ThinkMode'));
const ThinkNotes = lazy(() => import('./pages/ThinkNotes'));
const WorkNotes = lazy(() => import('./work/WorkNotes'));
const AuthoredRecovery = lazy(() => import('./components/think/AuthoredRecovery'));
const Settings = lazy(() => import('./pages/Settings'));
const Wiki = lazy(() => import('./pages/Wiki'));
/* Home is not code-split.
   /wiki is where the wordmark, / and /paper all land, so this is needed on
   every cold start — and splitting it meant the browser only asked for it
   after main.js had downloaded and parsed. That is a second round trip in
   series before anything can be drawn, with a full-screen splash held over it
   the whole way. A chunk you always need is not a chunk. */
const WikiArticle = lazy(() => import('./components/wiki/WikiArticle'));
const Contradictions = lazy(() => import('./pages/Contradictions'));
const ReadingMap = lazy(() => import('./pages/ReadingMap'));
const CommonplaceBook = lazy(() => import('./pages/CommonplaceBook'));
const Books = lazy(() => import('./pages/Books'));
/* Not code-split. /judgment is one of the four rooms and the one a citation
   trail ends in, so its chunk is asked for on a cold open — and splitting it
   means the browser only requests it after main.js has downloaded and parsed,
   a second round trip in series before any of the claim can be drawn. */
const WikiIngestRun = lazy(() => import('./pages/WikiIngestRun'));
const Welcome = lazy(() => import('./pages/Welcome'));
const ReviewerFixtures = lazy(() => import('./pages/ReviewerFixtures'));
const HowToUse = lazy(() => import('./pages/HowToUse'));
const Integrations = lazy(() => import('./pages/Integrations'));
const AgentConnectAuthorize = lazy(() => import('./pages/AgentConnectAuthorize'));
const ChatGPTConnectAuthorize = lazy(() => import('./pages/ChatGPTConnectAuthorize'));
const AgentTaskRun = lazy(() => import('./pages/AgentTaskRun'));
const GuideArticlePage = lazy(() => import('./components/seo/GuideArticlePage'));
const GuidesHub = lazy(() => import('./pages/GuidesHub'));
const Examples = lazy(() => import('./pages/Examples'));
const PublicProofGallery = lazy(() => import('./pages/PublicProofGallery'));
const MarketingAnalytics = lazy(() => import('./pages/MarketingAnalytics'));
const SearchConsoleOpportunities = lazy(() => import('./pages/SearchConsoleOpportunities'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfUse = lazy(() => import('./pages/TermsOfUse'));
const Support = lazy(() => import('./pages/Support'));
const OpenSentenceStoryboard = lazy(() => import('./pages/OpenSentenceStoryboard'));
const SharedQuestion = lazy(() => import('./pages/SharedQuestion'));
const SharedEdition = lazy(() => import('./pages/SharedEdition'));
const LentBook = lazy(() => import('./pages/LentBook'));
const RetiredShare = lazy(() => import('./pages/RetiredShare'));
const QuestionSharePreview = lazy(() => import('./pages/QuestionSharePreview'));
const SharedWikiPage = lazy(() => import('./pages/SharedWikiPage'));
const SharedWikiCollectionPage = lazy(() => import('./pages/SharedWikiCollectionPage'));
const PublicWikiComparison = lazy(() => import('./pages/PublicWikiComparison'));

/* A route that is still arriving is not an event.
   This used to be a full-viewport splash — an eyebrow, a headline in 3.4rem
   serif, and a shimmering bar — announcing that the wiki workspace was being
   prepared. Held for five seconds it read as the product's slowest moment
   dressed as its grandest. Now nothing is drawn at all until the wait is long
   enough to be worth admitting, and then it is one quiet line. */
const RouteLoadingFallback = () => (
  <div className="page-loading" role="status" aria-live="polite">
    <span className="page-loading__word">Still coming…</span>
  </div>
);

const scheduleDeferredStyleLoad = (callback) => {
  let frame = 0;
  let idle = 0;
  let timeout = 0;
  const run = () => {
    if (typeof window.requestIdleCallback === 'function') {
      idle = window.requestIdleCallback(callback, { timeout: 350 });
      return;
    }
    timeout = window.setTimeout(callback, 0);
  };
  if (typeof window.requestAnimationFrame === 'function') frame = window.requestAnimationFrame(run);
  else timeout = window.setTimeout(callback, 0);
  return () => {
    if (frame && typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(frame);
    if (idle && typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(idle);
    if (timeout) window.clearTimeout(timeout);
  };
};

const bootstrapDevTokenFromLocation = () => {
  if (process.env.NODE_ENV !== 'development') return false;
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search);
    const devToken = params.get('devToken');
    if (!devToken) return false;
    localStorage.setItem('token', devToken);
    localStorage.setItem('authToken', devToken);
    localStorage.setItem('jwt', devToken);
    return true;
  } catch (_error) {
    return false;
  }
};

/* Think has one face — the note — and a set of still-addressable postures
   behind it. Which one renders is decided by whether the URL names an object,
   so links from Library, Wiki and the palette keep landing where they point. */
const ThinkSurface = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const workId = params.get('explorationId');
  if (workId) return <AuthoredRecovery key={workId} workId={workId} />;
  // The handoff queue moved to Connections; old links follow it there.
  if (params.get('tab') === 'handoffs') {
    const handoffId = params.get('handoffId') || params.get('handoff');
    return <Navigate to={`/connections${handoffId ? `?handoffId=${encodeURIComponent(handoffId)}` : ''}#queue`} replace />;
  }
  return namesAThinkObject(location.search) ? <ThinkMode /> : <ThinkNotes />;
};

/* Rooms that were retired. Their addresses still resolve, to the place that
   now does the job: highlights and saved searches live in Library, export in
   Settings, and the review and map postures in Think. */
const RETIRED_ROOMS = [
  ['/all-highlights', '/library?scope=highlights'],
  ['/search', '/library'],
  ['/views/*', '/library'],
  ['/views', '/library'],
  ['/collections/*', '/library'],
  ['/collections', '/library'],
  ['/tags', '/think?tab=concepts'],
  ['/export', '/settings?section=data'],
  ['/map', '/think'],
  ['/review', '/think'],
  ['/return-queue', '/think'],
  ['/brain', '/think'],
  ['/resurface', '/think'],
  ['/journey', '/think']
];

const LegacyConceptRedirect = () => {
  const { tagName, tag } = useParams();
  const conceptName = String(tagName || tag || '').trim();
  return <Navigate to={buildThinkPosturePath('concepts', conceptName)} replace />;
};

// Books had a page inside the Library; links to it (old Morning Papers) still open the book.
const LegacyBookRedirect = () => {
  const { id = '' } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/books${id ? `/${id}` : ''}${search}`} replace />;
};

const LegacyNotebookRedirect = () => {
  const { entryId = '' } = useParams();
  return <Navigate to={buildThinkPosturePath('notebook', entryId)} replace />;
};

const LegacyQuestionRedirect = () => {
  const { questionId = '' } = useParams();
  return <Navigate to={buildThinkPosturePath('questions', questionId)} replace />;
};

/* An old article link that named a passage still opens at the passage. */
const LegacyArticleRedirect = () => {
  const { id } = useParams();
  const highlightId = new URLSearchParams(useLocation().search).get('highlightId') || '';
  return <Navigate to={buildCanonicalHighlightPath({ articleId: id, highlightId })} replace />;
};

const LegacyWikiPageRedirect = () => {
  const { id = '' } = useParams();
  const location = useLocation();
  const trimmedId = String(id).trim();
  const legacyParams = new URLSearchParams(location.search);
  const mode = legacyParams.get('mode') === 'edit' ? '&mode=edit' : '';
  const workspacePath = trimmedId
    ? `/wiki/workspace?page=${encodeURIComponent(trimmedId)}${mode}`
    : '/wiki/workspace';

  return <Navigate to={workspacePath} replace />;
};

const DataIntegrationsRedirect = () => {
  const location = useLocation();
  const hash = location.hash || '#sources';
  return <Navigate to={`/connections${location.search}${hash}`} replace />;
};

export const isPublicSharePath = (pathname = '') => pathname.startsWith('/share/');

export const isDesignPreviewPath = (pathname = '') => (
  pathname === '/design-preview' || pathname.startsWith('/design-preview/')
);

/* Pages that read the same signed in or out. They are drawn by the public
   tree either way, so they are declared once. */
const PUBLIC_PAGES = new Set(['/guides', '/examples', '/proof', '/privacy', '/terms', '/support', ...GUIDE_SLUGS.map((slug) => `/${slug}`)]);
export const isPublicPage = (pathname = '') => (
  isPublicSharePath(pathname) || isDesignPreviewPath(pathname) || PUBLIC_PAGES.has(pathname)
);

const PublicRoutes = ({ chromeStoreLink, handleLoginSuccess }) => {
  const location = useLocation();
  const isShareRoute = isPublicSharePath(location.pathname);
  const isLongformRoute = (
    GUIDE_SLUGS.includes(location.pathname.slice(1))
    || location.pathname === '/guides'
    || location.pathname === '/examples'
    || location.pathname === '/proof'
    || isDesignPreviewPath(location.pathname)
  );
  const isEditorialPublicRoute = (
    location.pathname === '/'
    || location.pathname === '/privacy'
    || location.pathname === '/terms'
    || location.pathname === '/support'
    || isShareRoute
    || isLongformRoute
  );
  const publicContainerClassName = [
    'auth-pages-container',
    isLongformRoute ? 'auth-pages-container--scroll' : '',
    isEditorialPublicRoute ? 'auth-pages-container--public' : ''
  ].filter(Boolean).join(' ');

  return (
    <div className={publicContainerClassName}>
      <Suspense fallback={<RouteLoadingFallback />}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/guides" element={<GuidesHub />} />
          <Route path="/examples" element={<Examples />} />
          <Route path="/proof" element={<PublicProofGallery />} />
          {GUIDE_SLUGS.map((slug) => (
            <Route key={slug} path={`/${slug}`} element={<GuideArticlePage slug={slug} />} />
          ))}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfUse />} />
          <Route path="/support" element={<Support />} />
          <Route path="/design-preview/open-sentence" element={<OpenSentenceStoryboard />} />
          <Route path="/design-preview/question-share" element={<QuestionSharePreview />} />
          <Route path="/share/concepts/:slug" element={<RetiredShare kind="concepts" />} />
          <Route path="/share/questions/:slug" element={<SharedQuestion />} />
          <Route path="/share/editions/:slug" element={<SharedEdition />} />
          <Route path="/share/books/:slug" element={<LentBook />} />
          <Route path="/share/notebooks/:slug" element={<RetiredShare kind="notebooks" />} />
          <Route path="/share/volumes/:slug" element={<RetiredShare kind="volumes" />} />
          <Route path="/share/wiki/collection/:idOrSlug" element={<SharedWikiCollectionPage />} />
          <Route path="/share/wiki/:idOrSlug/comparison" element={<PublicWikiComparison />} />
          <Route path="/share/wiki/:idOrSlug" element={<SharedWikiPage />} />
          <Route path="/settings/connected-agents/authorize" element={<AgentConnectAuthorize />} />
          <Route path="/settings/connected-agents/chatgpt" element={<ChatGPTConnectAuthorize />} />
          <Route path="/a/run/:taskId" element={<AgentTaskRun />} />
          <Route
            path="/register"
            element={(
              <Register
                chromeStoreLink={chromeStoreLink}
                onLoginSuccess={handleLoginSuccess}
              />
            )}
          />
          <Route
            path="/login"
            element={(
              <Login
                onLoginSuccess={handleLoginSuccess}
                chromeStoreLink={chromeStoreLink}
              />
            )}
          />
          <Route path="*" element={<PublicFallback />} />
        </Routes>
      </Suspense>
    </div>
  );
};

/* Two different answers to two different questions.
   A page of the product, reached without being signed in, sends you to sign in
   and remembers where you were going. Anything else does not exist and says
   so. Both used to be one redirect to the marketing home, which answered
   neither: a link to your own wiki and a mistyped URL both landed on a sales
   page, and nothing on it said why. */
const PublicFallback = () => {
  const location = useLocation();
  const wantsApp = isAppRoute(location.pathname);
  useEffect(() => {
    if (wantsApp) rememberReturnPath(location);
  }, [location, wantsApp]);
  if (wantsApp) return <Navigate to="/login" replace />;
  return <NotFound />;
};

const AuthenticatedLoginRedirect = () => {
  const [destination] = useState(readReturnPath);
  useEffect(forgetReturnPath, []);
  return <Navigate to={destination || '/'} replace />;
};

/* Stable authenticated runtime.
 *
 * These boundaries must live outside App. Defining them inside App creates a
 * new React component type whenever settings, tour state, or system status
 * changes; React then tears down the entire room, refetches its object, and
 * drops in-flight Agent work. The renderer may change as App state changes,
 * but this component identity does not. */
const AuthenticatedLayoutRuntime = ({ renderLayout, openPalette, setShortcutOverlayOpen }) => {
  const shellLocation = useLocation();
  const navigate = useNavigate();
  const { surface } = useNoeisSurfaceState();

  useEffect(() => {
    let primedAt = 0;
    let lapse = 0;
    /* While the chord is primed the masthead prints each room's letter. The
       class carries it rather than React state: a legend that appears on a
       keystroke should not re-render the room you are standing in. It drops
       on the next key, on the chord lapsing, and on the way out. */
    const showLetters = (primed) => {
      window.clearTimeout(lapse);
      document.body.classList.toggle(GO_TO_PRIMED_CLASS, primed);
      if (primed) {
        lapse = window.setTimeout(
          () => document.body.classList.remove(GO_TO_PRIMED_CLASS),
          GO_TO_CHORD_MS
        );
      }
    };
    const handleKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        openPalette();
        return;
      }

      const next = consumeGoToChord({ primedAt }, event);
      primedAt = next.primedAt;
      showLetters(Boolean(primedAt));
      if (next.spent) event.preventDefault();
      if (next.to) {
        event.stopPropagation();
        setShortcutOverlayOpen(false);
        navigate(next.to);
        return;
      }

      const isText = isGoToTypingTarget(event.target);
      if (isText) return;
      if (event.key === '?' && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        setShortcutOverlayOpen(true);
      }
    };
    /* The moves come after the room. A room listens on the document, which
       hears a key before the window does, and claims what it owns with
       preventDefault: J in the reader walks paragraphs, H on a nightstand row
       sends it home. The window takes only what no room claimed. */
    const moves = {
      previousRoom: () => navigate(adjacentRoom(window.location, -1)),
      nextRoom: () => navigate(adjacentRoom(window.location, 1)),
      nextItem: () => walkItems(1),
      previousItem: () => walkItems(-1),
      open: openFocusedItem,
      back: () => navigate(-1),
      forward: () => navigate(1),
      search: () => focusRoomSearch() || openPalette()
    };
    const handleMove = (event) => {
      const move = resolveMove(event);
      if (move && moves[move]() !== false) event.preventDefault();
    };
    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('keydown', handleMove);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('keydown', handleMove);
      window.clearTimeout(lapse);
      document.body.classList.remove(GO_TO_PRIMED_CLASS);
    };
  }, [navigate, openPalette, setShortcutOverlayOpen]);

  const storageFailure = useStorageStatus(true);
  return renderLayout({ shellLocation, navigate, surface, storageFailure });
};

const AuthenticatedAppRuntime = ({ renderLayout, openPalette, setShortcutOverlayOpen }) => (
  <TourProvider>
    <NoeisCapabilityProvider>
      <NoeisLoopProvider>
        <AgentRailProvider>
          <NoeisSurfaceProvider>
            <AuthenticatedLayoutRuntime
              renderLayout={renderLayout}
              openPalette={openPalette}
              setShortcutOverlayOpen={setShortcutOverlayOpen}
            />
          </NoeisSurfaceProvider>
        </AgentRailProvider>
      </NoeisLoopProvider>
    </NoeisCapabilityProvider>
  </TourProvider>
);

const AppRouterContent = ({
  isAuthenticated,
  publicRouteProps,
  renderLayout,
  openPalette,
  setShortcutOverlayOpen
}) => {
  const location = useLocation();
  const shouldUsePublicRoutes = !isAuthenticated || isPublicPage(location.pathname);

  if (shouldUsePublicRoutes) return <PublicRoutes {...publicRouteProps} />;
  /* Work lived at /think?context=work before it had a place in the bar. */
  if (location.pathname === '/think' && new URLSearchParams(location.search).get('context') === 'work') {
    const params = new URLSearchParams(location.search); params.delete('context');
    return <Navigate to={`/work?${params}`} replace />;
  }
  return (
    <AuthenticatedAppRuntime
      renderLayout={renderLayout}
      openPalette={openPalette}
      setShortcutOverlayOpen={setShortcutOverlayOpen}
    />
  );
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => (
    bootstrapDevTokenFromLocation() || hasUsableStoredToken()
  ));
  const [isLoading, setIsLoading] = useState(true);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const paletteReturnFocusRef = useRef(null);
  const [shortcutOverlayOpen, setShortcutOverlayOpen] = useState(false);
  const [productFeedbackOpen, setProductFeedbackOpen] = useState(false);
  const [themePickerOpen, setThemePickerOpen] = useState(false);
  const themeOwner = useRef(null);
  const openThemePicker = () => { themeOwner.current = localStorage.getItem('token'); setThemePickerOpen(true); };
  const [uiSettings, setUiSettings] = useState(() => loadUiSettingsFromStorage());
  const systemStatus = useSystemStatus();
  const {
    setBackgroundWork: setSystemBackgroundWork,
    setLatestReceipt: setSystemLatestReceipt,
    clearRecentReceipts: clearSystemRecentReceipts,
    setRecoverableFailure: setSystemRecoverableFailure,
    clearRecoverableFailure: clearSystemRecoverableFailure,
    resetSystemStatus
  } = systemStatus;
  const systemStatusControls = useMemo(() => ({
    setBackgroundWork: setSystemBackgroundWork,
    setLatestReceipt: setSystemLatestReceipt,
    clearRecentReceipts: clearSystemRecentReceipts,
    setRecoverableFailure: setSystemRecoverableFailure,
    clearRecoverableFailure: clearSystemRecoverableFailure,
    resetSystemStatus
  }), [
    setSystemBackgroundWork,
    setSystemLatestReceipt,
    clearSystemRecentReceipts,
    setSystemRecoverableFailure,
    clearSystemRecoverableFailure,
    resetSystemStatus
  ]);
  const systemStatusContextValue = useMemo(() => ({
    controls: systemStatusControls,
    snapshot: {
      backgroundWork: systemStatus.backgroundWork,
      latestReceipt: systemStatus.latestReceipt,
      recentReceipts: systemStatus.recentReceipts,
      recoverableFailure: systemStatus.recoverableFailure
    }
  }), [
    systemStatusControls,
    systemStatus.backgroundWork,
    systemStatus.latestReceipt,
    systemStatus.recentReceipts,
    systemStatus.recoverableFailure
  ]);

  // Your existing Chrome Store link
  const chromeStoreLink = "https://chromewebstore.google.com/detail/note-taker/bekllegjmjbnamphjnkifpijkhoiepaa?hl=en-US&utm_source=ext_sidebar";

  useEffect(() => (
    scheduleDeferredStyleLoad(() => {
      import('./styles/wiki.css');
    })
  ), []);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    try {
      const params = new URLSearchParams(window.location.search);
      const devToken = params.get('devToken');
      if (!devToken) return;
      params.delete('devToken');
      const nextQuery = params.toString();
      const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash || ''}`;
      window.history.replaceState({}, '', nextUrl);
      setIsAuthenticated(true);
    } catch (_error) {
      // Ignore malformed dev bootstrap parameters.
    }
  }, []);

  useEffect(() => {
    if (bootstrapDevTokenFromLocation() || hasUsableStoredToken()) {
      setIsAuthenticated(true);
    } else {
      clearStoredTokens();
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    // Add the editorial body class. Don't pin color-scheme inline — that
    // overrides the dark/light selectors in theme.css
    // and was part of why the dark mode toggle appeared to do nothing.
    // color-scheme is now set inside the theme CSS itself, gated on
    // data-ui-theme, so flipping the theme attribute carries it along.
    document.body.classList.add('noeis-editorial');
    return () => {
      document.body.classList.remove('noeis-editorial');
    };
  }, []);

  useLayoutEffect(() => {
    const normalized = applyUiSettingsToRoot(document.documentElement, uiSettings);
    persistUiSettingsToStorage(normalized);
  }, [uiSettings]);

  // Live-update on system theme change when user preference is 'auto'.
  // Explicit Light, Dark, and Tokyo Midnight ignore OS changes.
  useEffect(() => {
    if (uiSettings?.theme !== 'auto') return undefined;
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handle = () => {
      // Re-apply with the same settings; resolveActiveTheme will re-read mq.
      applyUiSettingsToRoot(document.documentElement, uiSettings);
    };
    if (mq.addEventListener) mq.addEventListener('change', handle);
    else if (mq.addListener) mq.addListener(handle);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handle);
      else if (mq.removeListener) mq.removeListener(handle);
    };
  }, [uiSettings]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    const ownerToken = localStorage.getItem('token');
    const syncUiSettings = async () => {
      try {
        const remote = await fetchUiSettings();
        if (cancelled || localStorage.getItem('token') !== ownerToken) return;
        const normalized = normalizeUiSettings(remote);
        setUiSettings(normalized);
        persistUiSettingsToStorage(normalized);
      } catch (error) {
        console.error('Failed to fetch UI settings:', error);
      }
    };
    syncUiSettings();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const handleLogout = () => {
    clearStoredTokens();
    setIsAuthenticated(false);
    persistUiSettingsToStorage(normalizeUiSettings(loadUiSettingsFromStorage()));
    window.location.href = '/';
  };

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
  };

  const handleAppearanceCommit = async (patch, nextDraft) => {
    const optimistic = normalizeUiSettings({ ...uiSettings, ...patch });
    const ownerToken = localStorage.getItem('token');
    try {
      const saved = await saveUiSettings(optimistic);
      if (localStorage.getItem('token') !== ownerToken) return { ok: false, stale: true };
      const normalized = normalizeUiSettings(saved);
      setUiSettings(normalized);
      persistUiSettingsToStorage(normalized);
      return { ok: true, settings: normalized };
    } catch (error) {
      if (localStorage.getItem('token') !== ownerToken) return { ok: false, stale: true };
      console.error('Failed to save appearance settings:', error);
      applyUiSettingsToRoot(document.documentElement, uiSettings);
      return { ok: false };
    }
  };

  const previewTheme = (theme) => { if (themeOwner.current === localStorage.getItem('token')) applyUiSettingsToRoot(document.documentElement, { ...uiSettings, theme }); };

  const closeThemePicker = async (theme) => {
    setThemePickerOpen(false);
    if (themeOwner.current !== localStorage.getItem('token')) return;
    if (theme === uiSettings.theme) {
      applyUiSettingsToRoot(document.documentElement, uiSettings);
      return;
    }
    systemStatus.clearRecoverableFailure();
    const result = await handleAppearanceCommit({ theme });
    if (!result.ok && !result.stale) {
      systemStatus.setRecoverableFailure({
        stage: 'Theme',
        message: `${formatAppearanceValue('theme', theme)} did not save, so the theme you had is back.`,
        retryable: true,
        retry: () => { closeThemePicker(theme); }
      });
    }
  };

  const handleAppearanceUndo = async (receipt) => {
    const undoPatch = {};
    receipt.keys.forEach((key) => {
      undoPatch[key] = receipt.before[key];
    });
    const stillCurrent = receipt.keys.every((key) => uiSettings[key] === receipt.after[key]);
    if (!stillCurrent) {
      return { ok: false, partial: true };
    }
    return handleAppearanceCommit(undoPatch, normalizeUiSettings({ ...uiSettings, ...undoPatch }));
  };

  const openPalette = useCallback(() => {
    if (document.activeElement instanceof HTMLElement) {
      paletteReturnFocusRef.current = document.activeElement;
    }
    setPaletteOpen(true);
  }, []);

  const closePalette = useCallback(() => {
    setPaletteOpen(false);
  }, []);

  useEffect(() => {
    if (paletteOpen) return undefined;
    const returnTarget = paletteReturnFocusRef.current;
    paletteReturnFocusRef.current = null;
    if (!returnTarget || !document.contains(returnTarget)) return undefined;
    window.setTimeout(() => returnTarget.focus(), 0);
    return undefined;
  }, [paletteOpen, closePalette]);

  useEffect(() => {
    if (!paletteOpen) return undefined;
    const getFocusable = () => Array.from(document.querySelectorAll(
      '.palette-overlay button, .palette-overlay [href], .palette-overlay input, .palette-overlay textarea, .palette-overlay select, .palette-overlay [tabindex]:not([tabindex="-1"])'
    )).filter((node) => !node.disabled && node.getAttribute('aria-hidden') !== 'true');
    const handlePointerDown = (event) => {
      if (event.target === document.querySelector('.palette-overlay')) {
        closePalette();
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closePalette();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = getFocusable();
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [paletteOpen, closePalette]);

  const primaryNavItems = getPrimaryNavItems();
  const secondaryNavItems = getSecondaryNavItems();

  if (isLoading) return <RouteLoadingFallback />;

  const renderAppLayout = ({ shellLocation, surface, storageFailure }) => {
    const recoverableFailure = systemStatus.recoverableFailure || storageFailure;
    const topBarAccountMenuItems = [
      {
        label: 'Feedback',
        onClick: () => setProductFeedbackOpen(true)
      },
      {
        label: 'Chrome Extension',
        href: chromeStoreLink,
        external: true
      },
      {
        label: 'Logout',
        onClick: handleLogout
      }
    ];
    const moreNavItems = secondaryNavItems.filter((item) => item.label !== 'Connections');
    const utilityNavItems = getTopBarUtilityNavItems();

    const routes = (
      <Page className="page-area">
        <CommandPalette open={paletteOpen} onClose={closePalette} />
        <KeyboardShortcutOverlay open={shortcutOverlayOpen} onClose={() => setShortcutOverlayOpen(false)} />
        <ProductFeedbackModal open={productFeedbackOpen} onClose={() => setProductFeedbackOpen(false)} />

        <TourManager />
        {/* A new account starts at the beginning, wherever it enters. */}
        <FirstRunGate />
        <Suspense fallback={<RouteLoadingFallback />}>
          <Routes>
            {/* One front page. The Paper is the top of the wiki now, so every
                way in — the wordmark, /, the old /paper — lands on the same
                page rather than on two that say overlapping things. */}
            <Route path="/" element={<Navigate to="/wiki" replace />} />
            <Route path="/paper" element={<Navigate to="/wiki" replace />} />
            {/* Today was a launcher for rooms that are now the nav itself. The
                route resolves so existing links keep working; it lands on the
                morning paper rather than on a menu. */}
            <Route path="/today" element={<Navigate to="/wiki" replace />} />
            <Route path="/library" element={<Library />} />
            <Route path="/library/trail" element={<LibraryTrail />} />
            <Route path="/library/save" element={<SaveLink />} />
            <Route path="/library/map" element={<ReadingMap />} />
            <Route path="/library/book" element={<CommonplaceBook />} />
            <Route path="/library/books" element={<LegacyBookRedirect />} />
            <Route path="/library/books/:id" element={<LegacyBookRedirect />} />
            <Route path="/books" element={<Books />} />
            <Route path="/books/:id" element={<Books />} />
            {/* Papers an agent maintains. Not a fifth room in the nav — the
                four rooms are what you did, and an edition is something that
                arrived. The Paper carries the door to it. */}
            <Route path="/editions" element={<Editions />} />
            <Route path="/editions/:id" element={<EditionRead />} />
            {/* Think opens the note you were last in. The legacy postures stay
                addressable: a URL that names a concept, question, thread or
                entry still opens that object in the older workspace. */}
            <Route path="/think" element={<ThinkSurface />} />
            <Route path="/work" element={<WorkNotes />} />
            {/* /wiki is the quiet collection; the maintenance workspace stays
                one hairline away at /wiki/workspace. */}
            {/* Judgment: the index is a list of claim sentences; opening one
                is the claim itself. */}
            <Route path="/judgment" element={<Judgment />} />
            <Route path="/judgment/mirror" element={<JudgmentMirror />} />
            <Route path="/mirror" element={<Navigate to="/judgment/mirror" replace />} />
            <Route path="/judgment/:pageId" element={<Judgment />} />
            {/* The week, gathered from marks the surfaces already carry. */}
            <Route path="/wiki" element={<WikiFrontPage />} />
            <Route path="/wiki/dossiers" element={<WikiFrontPage initialKind="investment" />} />
            {/* Contradiction as a view, not a tag: where the library
                disagrees with itself, both passages side by side. */}
            <Route path="/wiki/contradictions" element={<Contradictions />} />
            <Route path="/wiki/list" element={<Navigate to="/wiki/workspace?view=list" replace />} />
            {/* The reading. The operational workspace — chat pane, graph,
                queues — stays where it was, at /wiki/workspace. */}
            <Route path="/wiki/read/:id" element={<WikiArticle />} />
            <Route path="/wiki/workspace" element={<Wiki />} />
            <Route path="/wiki/activity/:runId" element={<WikiIngestRun />} />
            <Route path="/welcome" element={<Welcome />} />
            <Route path="/reviewer-fixtures" element={<ReviewerFixtures />} />
            <Route path="/wiki/:id" element={<LegacyWikiPageRedirect />} />
            <Route
              path="/settings"
              element={(
                <Settings
                  uiSettings={uiSettings}
                  onAppearanceCommit={handleAppearanceCommit}
                  onAppearanceUndo={handleAppearanceUndo}
                  onOpenThemePicker={openThemePicker}
                />
              )}
            />
            <Route path="/how-to-use" element={<HowToUse />} />
            <Route path="/connections" element={<Integrations />} />
            <Route path="/integrations" element={<Integrations />} />
            <Route path="/settings/connected-agents/authorize" element={<AgentConnectAuthorize />} />
            <Route path="/settings/connected-agents/chatgpt" element={<ChatGPTConnectAuthorize />} />
            <Route path="/a/run/:taskId" element={<AgentTaskRun />} />
            <Route path="/data-integrations" element={<DataIntegrationsRedirect />} />
            <Route path="/marketing-analytics" element={<MarketingAnalytics />} />
            <Route path="/search-console-opportunities" element={<SearchConsoleOpportunities />} />

            {/* Legacy/feature routes kept for compatibility */}
            {RETIRED_ROOMS.map(([path, to]) => (
              <Route key={path} path={path} element={<Navigate to={to} replace />} />
            ))}
            <Route path="/tags/:tagName" element={<LegacyConceptRedirect />} />
            <Route path="/concepts" element={<LegacyConceptRedirect />} />
            <Route path="/concepts/:tag" element={<LegacyConceptRedirect />} />
            <Route path="/notebook" element={<LegacyNotebookRedirect />} />
            <Route path="/notebook/:entryId" element={<LegacyNotebookRedirect />} />
            <Route path="/questions" element={<LegacyQuestionRedirect />} />
            <Route path="/questions/:questionId" element={<LegacyQuestionRedirect />} />
            <Route path="/question/:questionId" element={<LegacyQuestionRedirect />} />
            <Route path="/concept/:tag" element={<LegacyConceptRedirect />} />
            <Route path="/board" element={<Navigate to="/think?tab=concepts" replace />} />
            <Route path="/studio-board" element={<Navigate to="/think?tab=concepts" replace />} />
            <Route path="/boards" element={<Navigate to="/think?tab=concepts" replace />} />
            <Route path="/boards/*" element={<Navigate to="/think?tab=concepts" replace />} />
            <Route path="/articles/:id" element={<LegacyArticleRedirect />} />
            {/* Redirect authenticated users away from auth pages */}
            <Route path="/login" element={<AuthenticatedLoginRedirect />} />
            <Route path="/register" element={<Navigate to="/" replace />} />
            {/* Signed in, an unknown path rendered nothing at all — a top bar
                over an empty column, which reads as the page having failed. */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </Page>
    );

    return (
      <TemporaryAppearanceProvider
        committedSettings={uiSettings}
        onKeepTemporary={(draft) => handleAppearanceCommit(
          Object.fromEntries(
            Object.entries(draft).filter(([key, value]) => uiSettings[key] !== value)
          ),
          draft
        )}
      >
      <AppShell
        surface={surface}
        /* One Partner, the same in every room: ⌘J or its button opens it. */
        partner={hasPartnerPanel(shellLocation.pathname) ? <PartnerPanel /> : null}
        /* First run is one column and a way out; the rooms come after. */
        topBar={shellLocation.pathname === '/welcome' ? null : (
          <TopBar
            routeLocation={shellLocation}
            primaryNav={primaryNavItems}
            utilityNav={utilityNavItems}
            secondaryNav={moreNavItems}
            searchMode="field"
            onSearchOpen={openPalette}
            accountMenuItems={topBarAccountMenuItems}
            theme={{
              id: uiSettings.theme,
              label: formatAppearanceValue('theme', uiSettings.theme),
              onOpen: openThemePicker
            }}
            systemStatus={{
              backgroundWork: systemStatus.backgroundWork,
              latestReceipt: systemStatus.latestReceipt,
              recentReceipts: systemStatus.recentReceipts,
              clearRecentReceipts: systemStatus.clearRecentReceipts,
              recoverableFailure
            }}
            onSystemStatusRetry={() => {
              const retry = systemStatus.recoverableFailure?.retry;
              systemStatus.clearRecoverableFailure();
              if (typeof retry === 'function') {
                retry();
              }
            }}
            className=""
          />
        )}
      >
        {routes}
        <TemporaryAppearanceBar />
      </AppShell>
      </TemporaryAppearanceProvider>
    );
  };

  return (
    <SystemStatusProvider value={systemStatusContextValue}>
      <ThemePicker open={themePickerOpen} value={uiSettings.theme} onPreview={previewTheme} onConfirm={closeThemePicker} onCancel={closeThemePicker} />
      <Router>
        <Analytics />
        <AppRouterContent
          isAuthenticated={isAuthenticated}
          publicRouteProps={{ chromeStoreLink, handleLoginSuccess }}
          renderLayout={renderAppLayout}
          openPalette={openPalette}
          setShortcutOverlayOpen={setShortcutOverlayOpen}
        />
      </Router>
    </SystemStatusProvider>
  );
}

export default App;
